import { Signal, computed, signal } from '@angular/core';

export type PageItem = number | 'ellipsis';

const MAX_UNWINDOWED_PAGES = 7;

/**
 * Build the list of page controls to render: every page when there are few,
 * otherwise the first page, the last page, and a window around the current
 * page, with `'ellipsis'` markers standing in for the collapsed ranges.
 */
export function getPageItems(currentPage: number, totalPages: number): PageItem[] {
  if (totalPages <= MAX_UNWINDOWED_PAGES) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set<number>([1, totalPages]);
  for (let page = currentPage - 1; page <= currentPage + 1; page++) {
    if (page >= 1 && page <= totalPages) {
      pages.add(page);
    }
  }

  const sorted = [...pages].sort((a, b) => a - b);
  const items: PageItem[] = [];
  let previous: number | null = null;

  for (const page of sorted) {
    if (previous !== null && page - previous === 2) {
      // A gap of exactly one page — show the page itself instead of an ellipsis.
      items.push(previous + 1);
    } else if (previous !== null && page - previous > 2) {
      items.push('ellipsis');
    }

    items.push(page);
    previous = page;
  }

  return items;
}

/** Signal-based client-side pager over an in-memory list (see {@link createClientPager}). */
export interface ClientPager<T> {
  /** 1-based current page; only ever moved via `goToPage`/`reset`, always in range. */
  readonly currentPage: Signal<number>;
  readonly totalPages: Signal<number>;
  /** The window of source items for the current page. */
  readonly pagedItems: Signal<T[]>;
  /** Page controls to render (page numbers with ellipsis gaps). */
  readonly pageItems: Signal<PageItem[]>;
  /** Summary line, e.g. `Showing 1–5 of 12 requests`. */
  readonly showingSummary: Signal<string>;
  /** Clamp into range and navigate; no-op when already on the target page. */
  goToPage(page: number): void;
  /** Snap back to page 1 (e.g. after the source list is replaced by a reload). */
  reset(): void;
}

/**
 * Create pagination state over a signal of already-loaded items. `noun` is the
 * plural label used in the showing summary ("requests", "verifications", …).
 */
export function createClientPager<T>(
  source: Signal<T[]>,
  pageSize: number,
  noun: string,
): ClientPager<T> {
  const currentPage = signal(1);

  const totalPages = computed(() => {
    const total = source().length;
    return total === 0 ? 1 : Math.ceil(total / pageSize);
  });

  const pagedItems = computed(() => {
    const start = (currentPage() - 1) * pageSize;
    return source().slice(start, start + pageSize);
  });

  const pageItems = computed(() => getPageItems(currentPage(), totalPages()));

  const showingSummary = computed(() => {
    const total = source().length;

    if (total === 0) {
      return `Showing 0 of 0 ${noun}`;
    }

    const start = (currentPage() - 1) * pageSize + 1;
    const end = Math.min(start + pagedItems().length - 1, total);
    return `Showing ${start}–${end} of ${total} ${noun}`;
  });

  return {
    currentPage: currentPage.asReadonly(),
    totalPages,
    pagedItems,
    pageItems,
    showingSummary,
    goToPage(page: number): void {
      const nextPage = Math.max(1, Math.min(page, totalPages()));
      if (nextPage !== currentPage()) {
        currentPage.set(nextPage);
      }
    },
    reset(): void {
      currentPage.set(1);
    },
  };
}
